import {
  DatePicker,
  Dropdown,
  IDropdownOption,
  Spinner,
} from "@fluentui/react";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CustomCommandBar from "../../Components/CustomCommandBar/CustomCommandBar";
import Loadable from "../../Components/Loadable/Loadable";
import { IAlarm, isTestAlarm } from "../../Models/IAlarm";
import { ISentinelRich } from "../../Models/ISentinel";
import { GenericActions } from "../../Redux/Generic/GenericAction";
import { GlobalState } from "../../Redux/RootReducer";
import { IAPIResponse } from "../../Services/AjaxService";
import ApiService from "../../Services/ApiService";
import { ToastMessage } from "../../Utils/UIMessages";
import { Bar, Pie } from "react-chartjs-2";
import { Chart, ArcElement } from "chart.js/auto";
import Manipulators from "./StatisticPage.manipulators";
import { ISentinelTrace } from "../../Models/ISentinelTrace";
import "./StatisticPage.scss";
import NavigationMenu from "../../Components/NavigationMenu/NavigationMenu";
Chart.register(ArcElement);

export interface ISentinelTraceContainer {
  item: ISentinelTrace;
  sentinel: ISentinelRich;
}

const sentinelFromId = (id: number, sentinels: ISentinelRich[]) => {
  let s = sentinels.filter((x: ISentinelRich) => x.id === id);
  return s.length > 0 ? s[0] : null;
};

const htmlDateToDateObj = (date: string) => {
  var d = date.split("-");
  return new Date(parseInt(d[0]), parseInt(d[1]) - 1, parseInt(d[2]));
};

const dateIsInBetween = (date: string, min: string, max: string) => {
  var from = Date.parse(min as any);
  var to = Date.parse(max as any);
  var check = Date.parse(date as any);

  return check <= to && check >= from;
};

export const optionsPie = {
  elements: {
    bar: {
      borderWidth: 2,
    },
  },
  responsive: true,
  plugins: {
    title: {
      display: true,
      text: "Totale degli allarmi nel tempo",
    },
  },
};

export const optionsBar = {
  elements: {
    bar: {
      borderWidth: 2,
    },
  },
  responsive: true,
  plugins: {
    title: {
      display: true,
      text: "Distribuzione degli allarmi nel tempo",
    },
  },
};

export const optionsPieUsage = {
  elements: {
    bar: {
      borderWidth: 2,
    },
  },
  responsive: true,
  plugins: {
    title: {
      display: true,
      text: "Totale degli usi nel tempo",
    },
  },
};

export const optionsBarUsage = {
  elements: {
    bar: {
      borderWidth: 2,
    },
  },
  responsive: true,
  plugins: {
    title: {
      display: true,
      text: "Distribuzione degli usi nel tempo",
    },
  },
};

const getSisMonthAgoDate = () => {
  const SIX_MONTHS_AGO = new Date();
  SIX_MONTHS_AGO.setMonth(SIX_MONTHS_AGO.getMonth() - 6);
  return SIX_MONTHS_AGO;
};

const dateToPartialIso = (date: Date) => {
  return date.toISOString();
};

const StatisticPage = () => {
  const dispatch = useDispatch();
  const [analisysType, setAnalisysType] = useState<string>("alarms");
  const [aggregation, setAggregation] = useState<string>("0");
  const [patient, setPatient] = useState<number>(-1);
  const [dateFrom, setDateFrom] = useState<string>(
    dateToPartialIso(getSisMonthAgoDate())
  );
  const [alarms, setAlarms] = useState<IAlarm[]>([]);
  const [statusTraces, setStatusTraces] = useState<ISentinelTrace[]>([]);
  const [reloading, setReloading] = useState<boolean>(false);
  const [alarmsInRange, setAlarmsInRange] = useState<IAlarm[]>([]);
  const [statusTracesInRange, setStatusTracesInRange] = useState<
    ISentinelTrace[]
  >([]);

  const [dateTo, setDateTo] = useState<string>(dateToPartialIso(new Date()));
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingAlarms, setLoadingAlarms] = useState<boolean>(false);
  const [loadingTraces, setLoadingTraces] = useState<boolean>(false);

  const sentinels: ISentinelRich[] = useSelector(
    (s: GlobalState) => s.generic.sentinels || []
  );

  const doReloading = () => {
    setReloading(true);
    setTimeout(() => {
      setReloading(false);
    }, 1000);
  };

  const loadSentinels = () => {
    if (sentinels.length === 0) {
      setLoading(true);
    }
    ApiService.SentinelController.GetSentinels((response: IAPIResponse) => {
      if (response.error === null) {
        dispatch(GenericActions.setSentinels(response.parsed) as any);
        GetSentinelStatusService();
        SearchAlarmData();
      } else {
        ToastMessage("error", "Impossibile caricare i dispositivi.");
      }
      setLoading(false);
    });
  };

  const getPatientOptions = () => {
    let options: any[] = [
      ...sentinels.map((x: ISentinelRich) => {
        return {
          key: x.patient.id,
          text: x.patient.identifier,
        };
      }),
    ];
    return [{ key: -1, text: "Non filtrare (usa intero cluster)" }].concat(
      options
    );
  };

  const GetSentinelStatusService = () => {
    if (statusTraces.length === 0) {
      setLoadingTraces(true);
      ApiService.TraceController.ListStatusDataAll((response: IAPIResponse) => {
        if (response.error === null) {
          setStatusTraces(response.parsed);
        } else {
          ToastMessage(
            "error",
            "Impossibile caricare dati sugli utilizzi delle Letti."
          );
        }
        setLoadingTraces(false);
      });
    }
  };

  const SearchAlarmData = () => {
    if (alarms.length === 0) {
      setLoadingAlarms(true);
      ApiService.AlarmsController.GetAlarms((response: IAPIResponse) => {
        if (response.error === null) {
          // le prove lanciate da Setup non entrano nelle statistiche
          setAlarms(
            (response.parsed ?? []).filter((x: IAlarm) => !isTestAlarm(x))
          );
        } else {
          ToastMessage("error", "Impossibile caricare dati sugli allarmi.");
        }
        setLoadingAlarms(false);
      });
    }
  };

  const manageSpecialAlarms = (alarmsRaw: IAlarm[]) => {
    for (let i = 0; i < alarmsRaw.length; i++) {
      if (alarmsRaw[i].reason === "1 - Allarme rientrato") {
        alarmsRaw[i].sensor.type = "falseAlarm";
      }
    }
    return alarmsRaw;
  };

  const manageStatusData = (data: any[]) => {
    return data;
  };

  const FormatAlarmsData = () => {
    if (dateFrom && dateTo) {
      let d1 = new Date(dateFrom);
      let d2 = new Date(dateTo);
      if (d1.getFullYear() > 1900 && d2.getFullYear() > 1900) {
        setAlarmsInRange(
          manageSpecialAlarms(
            alarms.filter((x) => dateIsInBetween(x.createdAt, dateFrom, dateTo))
          )
        );
      }
    }
  };

  const FormatTraceData = () => {
    if (dateFrom && dateTo) {
      let d1 = new Date(dateFrom);
      let d2 = new Date(dateTo);
      if (d1.getFullYear() > 1900 && d2.getFullYear() > 1900) {
        setStatusTracesInRange(
          manageStatusData(
            statusTraces.filter((x) =>
              dateIsInBetween(x.createdAt, dateFrom, dateTo)
            )
          )
        );
      }
    }
  };

  useEffect(() => {
    FormatAlarmsData();
    FormatTraceData();
  }, [dateTo, dateFrom, alarms, statusTraces]);

  useEffect(() => {
    loadSentinels();
  }, []);

  return (
    <div className="fade-in-whole-page">
      <CustomCommandBar
        specials={[
          <div className="command-bar-title">Analisi statistica</div>,
          <NavigationMenu />,
        ]}
        buttons={[]}
      />
      <div className="sliding-page-content">
        <Loadable
          isLoading={loading || loadingAlarms || loadingTraces}
          content={
            <div>
              <div className="panel">
                <div className="statistic-page-input-wrap-line flex-column-on-small-screen">
                  <div className="statistic-page-input-wrap fill-width-on-small-screen">
                    <Dropdown
                      label="Tipo analisi"
                      placeholder="Tipo analisi"
                      selectedKey={analisysType}
                      onChange={(e: any, o: IDropdownOption | undefined) => {
                        if (o !== undefined) {
                          doReloading();
                          setAnalisysType(o.key.toString());
                        }
                      }}
                      options={[
                        { key: "alarms", text: "Allarmi" },
                        { key: "usage", text: "Utilizzo Letti" },
                      ]}
                    />
                  </div>
                  <div className="statistic-page-input-wrap fill-width-on-small-screen">
                    <DatePicker
                      value={new Date(dateFrom)}
                      placeholder="Dalla mattina del"
                      label="Dalla mattina del"
                      onSelectDate={(date: Date | null | undefined) => {
                        if (date) {
                          doReloading();
                          setDateFrom(dateToPartialIso(date));
                        }
                      }}
                      maxDate={new Date(dateTo)}
                    />
                  </div>
                  <div className="statistic-page-input-wrap fill-width-on-small-screen">
                    <DatePicker
                      value={new Date(dateTo)}
                      placeholder="Alla mattina del"
                      label="Alla mattina del"
                      onSelectDate={(date: Date | null | undefined) => {
                        if (date) {
                          doReloading();
                          setDateTo(dateToPartialIso(date));
                        }
                      }}
                      minDate={new Date(dateFrom)}
                    />
                  </div>
                  <div className="statistic-page-input-wrap fill-width-on-small-screen">
                    <Dropdown
                      label="Ospite"
                      placeholder="Ospite"
                      selectedKey={patient}
                      onChange={(e: any, o: IDropdownOption | undefined) => {
                        if (o !== undefined) {
                          doReloading();
                          setPatient(+o.key);
                        }
                      }}
                      options={getPatientOptions()}
                    />
                  </div>
                  <div className="statistic-page-input-wrap fill-width-on-small-screen">
                    <Dropdown
                      label="Aggrega dati"
                      placeholder="Aggrega dati"
                      selectedKey={aggregation}
                      onChange={(e: any, o: IDropdownOption | undefined) => {
                        if (o !== undefined) {
                          doReloading();
                          setAggregation(o.key.toString());
                        }
                      }}
                      options={[
                        { key: "0", text: "Per mese" },
                        { key: "1", text: "Per trimestre" },
                        { key: "2", text: "Per semestre" },
                      ]}
                    />
                  </div>
                </div>
              </div>
              {reloading && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-around",
                    padding: "5em",
                  }}
                >
                  <Spinner label="Rendering..." />
                </div>
              )}
              {!reloading &&
                analisysType === "alarms" &&
                alarmsInRange.length > 0 && (
                  <div className="statistic-page-chart-wrapper-outer flex-column-on-small-screen">
                    <div className="statistic-page-chart-wrapper">
                      <Pie
                        options={optionsPie}
                        data={Manipulators.alarmsToPieChartData(
                          alarmsInRange,
                          patient
                        )}
                      />
                    </div>
                    <div className="statistic-page-chart-wrapper">
                      <Bar
                        options={optionsBar}
                        data={Manipulators.alarmsToBarChartData(
                          alarmsInRange,
                          patient,
                          dateFrom,
                          dateTo,
                          aggregation
                        )}
                      />
                    </div>
                  </div>
                )}

              {!reloading &&
                analisysType === "usage" &&
                statusTracesInRange.length > 0 && (
                  <div className="statistic-page-chart-wrapper-outer flex-column-on-small-screen">
                    <div className="statistic-page-chart-wrapper">
                      <Pie
                        options={optionsPieUsage}
                        data={Manipulators.usageToPieChartData(
                          statusTracesInRange
                            .map((x: ISentinelTrace) => {
                              return {
                                item: x,
                                sentinel: sentinelFromId(
                                  x.sentinelId,
                                  sentinels
                                ),
                              } as ISentinelTraceContainer;
                            })
                            .filter((x: ISentinelTraceContainer) => x.sentinel),
                          patient
                        )}
                      />
                    </div>
                    <div className="statistic-page-chart-wrapper">
                      <Bar
                        options={optionsBarUsage}
                        data={Manipulators.usageToBarChartData(
                          statusTracesInRange
                            .map((x: ISentinelTrace) => {
                              return {
                                item: x,
                                sentinel: sentinelFromId(
                                  x.sentinelId,
                                  sentinels
                                ),
                              } as ISentinelTraceContainer;
                            })
                            .filter((x: ISentinelTraceContainer) => x.sentinel),
                          patient,
                          dateFrom,
                          dateTo,
                          aggregation
                        )}
                      />
                    </div>
                  </div>
                )}
            </div>
          }
        />
      </div>
    </div>
  );
};

export default StatisticPage;
